<script lang="ts">
	//Svelte hooks
	import { onMount, onDestroy } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import {
		search_bluetooth_devices,
		stop_bluetooth_search_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		disconnect_from_device_android,
		search_bluetooth_devices_android,
		stop_bluetooth_search_android,
	} from 'src/services/native_container_requests_android';
	import { mobile } from 'src/services/mobile-rework';
	//Stores
	import { devices } from 'src/stores/lists/devices';
import { app, os } from 'src/stores/session/native-bridge';
	import { discoveredBleDevices, advertisingBleDevices, inclusionState } from 'src/stores/features/inclusion';
	import { deviceMainFilter } from 'src/stores/ui/filters';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { isDiffuser, moduleTransformId } from 'src/helpers/interface_helpers';
	//Components
	//Design system components
	import {
		CardGrid,
		SelectCard,
		Loader,
		Text,
		Input,
		Select,
		Alert,
		Label,
		Stack,
		Popover,
	} from '@Shelly/shelly-design-system';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	import { MODULES } from 'src/constants';
	import type { Device } from 'src/types/Device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let inclusionData;

	const isIOS = $app && $os === 'IOS';

	const scanForDevices = async () => {
		discoveredBleDevices.set({});
		if (!$app) return;
		if (isIOS) {
			search_bluetooth_devices();
		} else {
			search_bluetooth_devices_android();
		}
	};

	const stopScanOnBack = () => {
		if (isIOS) {
			stop_bluetooth_search_ios();
		} else {
			stop_bluetooth_search_android();
		}
	};

	onMount(() => {
		if (!isIOS && inclusionData.error && $inclusionState.id == 18) {
			disconnect_from_device_android();
		}
		if (!inclusionData.bluetooth.scanStarted) {
			inclusionData.bluetooth.scanStarted = true;
			scanForDevices();
		}
		if ($app && !isIOS) {
			mobile.addEventListener('back_button', stopScanOnBack);
		}
	});

	onDestroy(() => {
		clearTimeout(bleDebounceTimer);
		bleUnsubscribe();
		deviceMainFilter.set('');
		if ($app && !isIOS) {
			mobile.removeEventListener('back_button', stopScanOnBack);
		}
	});

	const handleDeviceSelect = (device) => {
		// Locked devices (BLE adv "locked" flag, FW-2493) reject unpaired BLE RPC and
		// cannot be included — surface the state but block selection (FE-2179).
		if (device.locked) return;
		inclusionData.selected = device;
	};
	interface SelectOptionPropsTypeExtended extends SelectOptionPropsType {
		translationDefault?: string;
		value: string;
	}

	const defaultAllDiscoveredFilterOption: SelectOptionPropsTypeExtended = {
		title: 'filter_all_discovered',
		value: '',
		translationDefault: 'All devices',
	};

	const defaultNewDevicesFilterOption: SelectOptionPropsTypeExtended = {
		title: 'filter_new_discovered',
		value: 'new',
		translationDefault: 'New devices',
	};

	const defaultUserOwnedFilterOption: SelectOptionPropsTypeExtended = {
		title: 'filter_mine_discovered',
		value: 'owned',
		translationDefault: 'My devices',
	};

	const ownedOptions: SelectOptionPropsTypeExtended[] = [
		{
			...defaultNewDevicesFilterOption,
			title: $format(defaultNewDevicesFilterOption.title, {
				default: defaultNewDevicesFilterOption.translationDefault,
			}),
		},
		{
			...defaultUserOwnedFilterOption,
			title: $format(defaultUserOwnedFilterOption.title, {
				default: defaultUserOwnedFilterOption.translationDefault,
			}),
		},
		{
			...defaultAllDiscoveredFilterOption,
			title: $format(defaultAllDiscoveredFilterOption.title, {
				default: defaultAllDiscoveredFilterOption.translationDefault,
			}),
		},
	];

	// initiate to this value so as to translate it via $format
	let devicesOwnedFilter = {
		...defaultNewDevicesFilterOption,
		title: $format(defaultNewDevicesFilterOption.title, {
			default: defaultNewDevicesFilterOption.translationDefault,
		}),
	};

	const filterByOwnership = (list, filter) => {
		// First filter by search term if present
		const searchTerm = $deviceMainFilter.toLowerCase().trim();
		let filteredBySearch = list;

		if (searchTerm !== '') {
			filteredBySearch = list.filter((d) => {
				const deviceName = ('Shelly ' + (d.name || '')).toLowerCase();
				if (deviceName.includes(searchTerm)) return true;

				const deviceID = d.id ? d.id.toLowerCase() : '';
				if (deviceID.includes(searchTerm)) return true;

				const displayID = d.displayID ? d.displayID.toLowerCase() : '';
				if (displayID.includes(searchTerm)) return true;

				return false;
			});
		}

		return filteredBySearch.filter((d) => {
			const deviceID =
				d.gen == 'GBLE' ? d.id : d?.jti ? moduleTransformId(d.id, d?.jti) : d.id.toLowerCase();
			const jumbledID = typeof d.jumbledID !== 'undefined' ? d.jumbledID.toLowerCase() : '';
			d.displayID = deviceID;
			/**
			 * Logic to filter out devices that are already in the account or ones that are missing.
			 * Devices in the app storage list are stored via lower case of the device Wi-Fi Mac Address.
			 *
			 * This logic also contains the backwards compatibility (jumbledID) for jumbled Mac returned via
			 * Bluetooth scan for Gen.2 devices with firmware versions between 0.14.** to 1.0.3.
			 */
			if (filter == 'new') {
				return (
					typeof $devices[deviceID] === 'undefined' &&
					typeof $devices[jumbledID] === 'undefined'
				);
			} else if (filter == 'owned') {
				if (typeof $devices[deviceID] !== 'undefined') {
					d.displayID = deviceID;
					return d;
				} else if (typeof $devices[jumbledID] !== 'undefined') {
					d.displayID = jumbledID;
					return d;
				}
			} else if (filter == '') {
				return d;
			}
		});
	};

	// Debounce BLE store via plain subscription — avoids reactive loop from reading the timer var in $: blocks.
	// 250ms reduces re-renders during active scanning while keeping the list feeling live.
	let bleDebounceTimer: ReturnType<typeof setTimeout>;
	let debouncedBleDevices: Record<string, any> = $discoveredBleDevices;
	const bleUnsubscribe = discoveredBleDevices.subscribe((value) => {
		clearTimeout(bleDebounceTimer);
		bleDebounceTimer = setTimeout(() => {
			debouncedBleDevices = value;
		}, 250);
	});

	// Explicit let variable for the active filter — guarantees $: listedDevices re-runs immediately
	// on filter change regardless of how the Design System Select updates its bound value.
	let activeFilter = devicesOwnedFilter?.value;
	const onFilterChange = () => {
		activeFilter = devicesOwnedFilter?.value;
	};

	// Tracks which locked device's explanation popover is open (keyed by device id),
	// so only one popover shows at a time within the discovered list.
	let openLockInfoId: string | undefined;
	const toggleLockInfo = (id: string) => {
		openLockInfoId = openLockInfoId === id ? undefined : id;
	};

	$: bleDeviceCount = Object.keys(debouncedBleDevices).length;
	$: listedDevices = filterByOwnership(Object.values(debouncedBleDevices), activeFilter);

	const emptyFilteredListTexts = {
		new: $format('inclusion_ble_empty_filter_new', {
			default: 'No new devices found nearby.',
		}),
		owned: $format('inclusion_ble_empty_filter_owned', {
			default: 'No owned devices found nearby.',
		}),
	};

	const getDeviceImage = (device: Device) => {
		if (device.icon) {
			return undefined;
		} else if (/^(https?:\/\/)/.test(device.image)) {
			return device.image;
		}
		return `images/device_images/${device.service_type || device.type}.png`;
	};

	const getDeviceName = (device: Device) => {
		if (MODULES.includes(device.type) || isDiffuser(device.type)) {
			return device.name;
		}
		return `Shelly ${device.name}`;
	};

	$: emptyFilteredListText = emptyFilteredListTexts[activeFilter];
</script>

<Text element="h2" class="mb-s">
	{$format('inclusion_ble_device_type', {
		default: 'Bluetooth scan',
	})}
</Text>

{#if inclusionData.bluetooth.scanStarted && bleDeviceCount <= 0}
	<Text class="mb-m" styleType="body-small">
		{$format('inclusion_ble_searching', {
			default: 'Searching for nearby Bluetooth devices.',
		})}
	</Text>

	<Alert
		class="mb-xl"
		type="info"
		isHeadingBold
		heading={$format('label_info')}
		message={$format('alert_message_ble_discovering_description', {
			default: 'Discovering ble devices requires single button push to appear in device list.',
		})}
	/>
	<Loader size="l" class="d-flex justify-content-center" />
{:else if bleDeviceCount == 0}
	<Text class="mb-s" styleType="body-small">
		{$format('inclusion_ble_empty', {
			default: 'No Bluetooth devices found nearby.',
		})}
	</Text>
{:else}
	<Text class="mb-s" styleType="body-small">
		{$format('inclusion_ble_list', {
			default: 'Select the device you want to include.',
		})}
	</Text>

	<Stack gap="s" class="mb-m" direction="column" directionMobile="row" alignMobile="none">
		<Select
			aria-labelledby="device-owned-filter"
			id="device-owned-filter"
			class="width-full"
			bind:value={devicesOwnedFilter}
			options={ownedOptions}
			icon="fas fa-filter"
			label={$format(defaultNewDevicesFilterOption.title, {
				default: defaultNewDevicesFilterOption.translationDefault,
			})}
			on:change={onFilterChange}
		/>
		<Input
			class="width-full"
			aria-labelledby="input-device-search"
			id="input-device-search"
			type="text"
			label={capFirstChar($format('search', { default: 'search' }))}
			icon="fas fa-search"
			bind:value={$deviceMainFilter}
			disabled={listedDevices.length == 0 && $deviceMainFilter == ''}
		/>
	</Stack>

	<!-- Alex: can be simplified by merging empty list descriptions, will do at a later time -->
	{#if listedDevices.length == 0 && $deviceMainFilter == ''}
		<Text class="mb-s " styleType="body-small">
			{emptyFilteredListText}
		</Text>
	{:else if listedDevices.length == 0 && $deviceMainFilter !== ''}
		<Text class="mb-s " styleType="body-small">
			{$format('inclusion_ble_empty_filter_search', {
				default: `No devices found while searching for: ${$deviceMainFilter}`,
				values: { searchValue: $deviceMainFilter },
			})}
		</Text>
	{:else}
		<CardGrid lg={290} sm={270} noPaddingBottom gridGap="s">
			{#each listedDevices as discoveredDevice}
				<SelectCard
					cardBgColor={$showNewLayout ? 'color-1' : undefined}
					class="width-full"
					type="horizontal"
					image={getDeviceImage(discoveredDevice)}
					title={getDeviceName(discoveredDevice)}
					icon={discoveredDevice.icon || undefined}
					size="s"
					descriptiveText={`${discoveredDevice.displayID}`}
					variant={'radio'}
					disabled={discoveredDevice.locked}
					selected={inclusionData.selected === discoveredDevice}
					on:click={() => handleDeviceSelect(discoveredDevice)}
				>
					<svelte:fragment slot="rightBadge">
						{#if discoveredDevice.locked}
							<Popover
								hideCloseBtn
								placement="top"
								show={openLockInfoId === discoveredDevice.id}
								on:close={() => (openLockInfoId = undefined)}
							>
								<Label
									size="s"
									icon="fas fa-info-circle"
									iconColor="--color-primary"
									noRadiusType="third"
									on:click={(e) => {
										e.stopPropagation();
										toggleLockInfo(discoveredDevice.id);
									}}
									class="cursor-pointer"
								/>
								<div slot="customContent">
									<Text styleType="body-extra-small">
										{$format('inclusion_ble_device_locked', {
											default:
												'The device provisioning window has expired. Please power cycle the device to refresh it.',
										})}
									</Text>
								</div>
							</Popover>
							<Label
								icon="fas fa-lock"
								type="warning"
								size="s"
								value={$format('label_locked', {
									default: 'Provisioning locked',
								})}
								noRadiusType="first"
							/>
						{:else if discoveredDevice.gen === 'GBLE' && $advertisingBleDevices.indexOf(discoveredDevice.id) > -1}
							<TransitionWrapper inDuration={200}>
								<Label
									icon={`fas fa-dot-circle`}
									type="success"
									size="s"
									value={''}
									noRadiusType="first"
								/>
							</TransitionWrapper>
						{/if}
					</svelte:fragment>
				</SelectCard>
			{/each}
		</CardGrid>
	{/if}
{/if}
